import { Device } from '@capacitor/device';
import { EnvironmentConfig, getFlag, getString } from '../../utils/environmentUtils';
import { AuthCache, UserPropertyKeys } from '@otuvy/auth';
import OneSignal from '@onesignal/capacitor-plugin';

export const handleNotificationSetup = async () => {
  if (getFlag(EnvironmentConfig.NOTIFICATIONS)) {
    const device = await Device.getInfo();
    if (device && device.platform !== 'web') {
      const userId: string | undefined = AuthCache.getCurrentUserId();
      const orgId = AuthCache.getCachedUserAttribute(UserPropertyKeys.organizationId);

      console.log(`OneSignal - Setting OneSignal external user id to ${userId}`);
      OneSignal.setConsentGiven(true); //TODO: this is a GDPR thing, we need to make sure we have a way to get consent from the user

      // This runs during sign in, before HomePage mounts and initializes OneSignal, so the SDK
      // may not be initialized yet. Every OneSignal call below (login, addTags, optIn) throws
      // natively when it is not, and Capacitor rethrows that on its plugin thread, killing the
      // process. initialize() is idempotent, so HomePage initializing again later is harmless.
      const oneSignalAppId = getString(EnvironmentConfig.ONESIGNAL_APP_ID);
      if (!oneSignalAppId) {
        console.warn('OneSignal - No App ID found!  Skipping notification setup');
        return;
      }
      await OneSignal.initialize(oneSignalAppId);

      if (userId) {
        await OneSignal.login(userId);
        // Log OneSignal-assigned IDs for support correlation. Push delivery is keyed
        // off these on OneSignal's backend; if a customer reports missing pushes, the
        // OneSignal dashboard can be searched directly by oneSignalId or subscriptionId.
        // Both can be null briefly after login before the backend round-trip completes.
        try {
          const oneSignalId = await OneSignal.User.getOnesignalId();
          const subscriptionId = await OneSignal.User.pushSubscription.getIdAsync();
          console.log('OneSignal - logged in:', { externalId: userId, oneSignalId, subscriptionId });
        } catch (idError) {
          // Diagnostic logging only; do not fail the setup flow if ID retrieval errors.
          console.log('OneSignal - logged in with external user id', userId, '(ID retrieval failed)', idError);
        }
      } else {
        console.error('No userId to set in OneSignal');
      }

      console.log(`Attempting to set OneSignal tags for userId ${userId} and orgId ${orgId}`);

      //Setting a tag a value of undefined/unknown removes it
      await OneSignal.User.addTags({
        userId: `${userId}`, //ID should always be known
        orgId: orgId ? orgId : 'Unknown', //Due to some bugs the org ID is not set on early adopters of the app so could be empty
      });
      await OneSignal.User.pushSubscription.optIn();
    }
  }
};

export const handleNotificationTeardown = async () => {
  if (getFlag(EnvironmentConfig.NOTIFICATIONS)) {
    try {
      const device = await Device.getInfo();

      //To prevent issues accessing OneSignal from the web
      if (device && device.platform !== 'web') await OneSignal.User.pushSubscription.optOut();
    } catch (error) {
      console.error('Error disabling push notifications', error);
    }
  }
};
